import React, { useEffect, useState } from 'react';
import { IInjectedStylingProps, withStyling } from './higher-order';
import clsx from 'clsx';
import DatePickerComponent, { registerLocale } from 'react-datepicker';
import nl from 'date-fns/locale/nl';
import { dateFormatList } from './date-picker-format-list';

import styles from './date-picker.module.scss';

registerLocale('nl', nl);

export interface IDatePickerProps extends IInjectedStylingProps {
	id: string;
	includeMonthDropdown?: boolean;
	includeTimeInput?: boolean;
	includeYearDropdown?: boolean;
	initDateFormat?: string;
	minDate?: Date;
	onChange: (id: string, date?: Date | null) => void;
	openToDate?: Date;
	placeholderText?: string;
	timeFormat?: string;
	timeInputLabel?: string;
	timeIntervals?: number;
	value?: Date | string | null;
	neverOpen?: boolean;
	onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
	onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
	name?: string | null;
}

const DatePickerWrapper = (props: IDatePickerProps): React.JSX.Element => {
	const [startDate, setStartDate] = useState<Date | null>(null);
	const initDateFormat = props.initDateFormat || `dd MMM yyyy${props.includeTimeInput ? ' - HH:mm' : ''}`;
	const timeFormat = props.timeFormat || 'HH:mm';
	const timeInputLabel = props.timeInputLabel || 'Tijd';
	const { value, onChange, ...otherProps } = props;

	useEffect(() => {
		setStartDate(getDateValue(value));
	}, [value]);

	const handleChange = (date: Date | null): void => {
		onChange(props.id, date);
	};

	return (
		<div className={clsx(props.styling?.getStyle(styles, 'DatePickerComponent'))}>
			<DatePickerComponent
				{...otherProps}
				name={props.name || undefined}
				id={props.id}
				onChange={handleChange}
				selected={startDate}
				placeholderText={props.placeholderText}
				showMonthDropdown={props.includeMonthDropdown}
				showYearDropdown={props.includeYearDropdown}
				showTimeInput={props.includeTimeInput}
				timeIntervals={props.timeIntervals}
				timeFormat={timeFormat}
				timeInputLabel={timeInputLabel}
				dateFormat={dateFormatList(initDateFormat, props.includeTimeInput)}
				locale="nl"
				minDate={props.minDate ?? new Date(1900, 1, 1)}
				open={props.neverOpen ? false : undefined}
				onFocus={props.onFocus}
				onBlur={props.onBlur}
			/>
		</div>
	);
};

const getDateValue = (date?: Date | string | null): Date | null => {
	if (!date) return null;

	if (typeof date === 'object') {
		return date;
	}

	return new Date(date);
};

const DatePicker = withStyling(DatePickerWrapper);
export { DatePicker };
